import { useMemo, useEffect } from "react";
import { useParams } from "react-router";
import { useForm } from "react-hook-form";

import Title from "./Title";
import IntiateForm from "./IntiateForm";
import { DeviationProps } from "./interface";
import Button from "../../components/atomic/Button";

import { PAGES_LIST } from "../../shared/utils/constants";
import { notify } from "../../shared/context/AlertContext";
import GetAuthContext from "../../shared/context/AuthContext";
import { getFormattedCurrency } from "../../shared/utils/Global";
import { LoanApplicationService } from "../../../unified-dashboard/src/services/LoanApplication";

interface DeviationFieldConfig {
  originalValue: string | number;
  proposedValue?: string | number;
  label: string;
  validation: "ROI" | "MONTHS" | "PROCESSINGFEE" | "NONNEGATIVE";
  isDisabled?: boolean;
}

type DeviationFields = Record<string, DeviationFieldConfig>;

const FIELDS: DeviationFields = {
  annualInterest: {
    originalValue: "",
    validation: "ROI",
    label: "Interest Rate",
    isDisabled: false,
  },
  processingFee: {
    originalValue: "",
    label: "Processing Fee",
    validation: "PROCESSINGFEE",
    isDisabled: false,
  },
};

export default function Intiate(props: DeviationProps) {
  const {
    watch,
    setValue,
    register,
    getValues,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm();

  const formValues = watch();
  const { sourceEntityID, loanID } = useParams();

  const { fetchToken } = GetAuthContext();
  const token = fetchToken();

  const isABFLBL = props?.isABFLBL;
  const isABFLPL = props?.isABFLPL;

  const loanOffer = props?.loanOfferDetails?.[0];

  Object.keys(FIELDS ?? {})?.forEach((key: string) => {
    FIELDS[key]["originalValue"] = loanOffer[key];
    FIELDS[key]["proposedValue"] = loanOffer[key];
  });

  // Set form values after FIELDS is populated
  useEffect(() => {
    if (loanOffer && (isABFLPL || isABFLBL)) {
      Object.keys(FIELDS).forEach((key: string) => {
        setValue(`original.${key}`, FIELDS[key].originalValue);
        setValue(`proposed.${key}`, FIELDS[key].proposedValue);
      });

      setValue("proposed.tenureMonths", loanOffer?.tenureMonths);
    }
  }, [loanOffer, setValue]);

  useEffect(() => {
    if (loanOffer?.tenureMonths && isABFLBL) {
      setValue("proposed.tenureMonths", loanOffer.tenureMonths);
      setValue("original.tenureMonths", loanOffer.tenureMonths);
    }
  }, [loanOffer?.tenureMonths, setValue]);

  const onSubmitHandler = async (data: any) => {
    let response;

    if (isABFLPL) {
      const payloadInfo = [
        {
          event: "offer_negotiation",
          identifier_type: "LOAN_APPLICATION_ID",
          identifier_id: loanID as string,
          additional_data: {
            remarks: String(data?.Remarks ?? ""),
            task: {
              metadata: {
                offerNegotiationParams: {
                  previousInterestRate: Number(data?.original?.annualInterest),
                  proposedInterestRate: Number(
                    data?.proposed?.annualInterest ?? "",
                  ),
                  previousProcessingFee: Number(
                    data?.original?.processingFee ?? "",
                  ),
                  proposedProcessingFee: Number(
                    data?.proposed?.processingFee ?? "",
                  ),
                },
              },
            },
          },
        },
      ];

      const payload = {
        token,
        payload: payloadInfo,
        pageName: PAGES_LIST.LOAN_APPLICATION_LOAN_TERM_DEVIATION_TAB,
        isLenderDashboard: props.isLenderDashboard,
        sourceEntityID,
      };

      response = await LoanApplicationService.handleEvent(payload);
    }

    if (isABFLBL) {
      const payloadInfo = {
        loanApplicationID: loanID as string,
        loanOfferID: loanOffer?.loanOfferID,
        loanAmount: {
          previous: String(loanOffer?.amount ?? ""),
          proposed: String(loanOffer?.amount ?? ""),
        },
        processingFee: {
          previous: String(data?.original?.processingFee ?? ""),
          proposed: String(data?.proposed?.processingFee ?? ""),
        },
        interestRate: {
          previous: String(data?.original?.annualInterest ?? ""),
          proposed: String(data?.proposed?.annualInterest ?? ""),
        },
        tenure: {
          previous: String(loanOffer?.tenureMonths ?? ""),
          proposed: String(loanOffer?.tenureMonths ?? ""),
        },
        comments: data?.comments,
        reasonForDeviation: "",
        requestID: "",
        requestAction: "raised",
        actionByGroupName: "",
        status: "",
        currentStatus: "",
        createdAt: "",
        viewOnly: false,
        priorityGroup: "",
        nextSteps: [],
      };

      const payload = {
        token: fetchToken(),
        payload: payloadInfo,
        loanApplicationID: loanID,
        pageName: PAGES_LIST.LOAN_APPLICATION_LOAN_TERM_DEVIATION_TAB,
      };

      response = await LoanApplicationService.saveLoanTermDeviation(payload);
    }

    if (response?.status) {
      if (isABFLPL) props?.setIsSliderOpen(false);

      notify({
        type: "success",
        title: "Success",
        text: "Loan Term Deviation Intiated Proposal Successfully",
      });

      setTimeout(() => window.location.reload(), 1000);
    } else {
      notify({
        title: "Error",
        type: "error",
        text: response.error || "Couldn't save Loan Term Deviation",
      });
    }
  };

  const isDisabled = useMemo(() => {
    let allEqual;
    const keys = ["annualInterest", "processingFee"] as const;
    const { original = {}, proposed = {} } = (formValues as any) || {};

    if (
      isABFLBL &&
      !proposed?.amount &&
      !proposed?.annualInterest &&
      !proposed?.processingFee
    ) {
      return true;
    } else {
      // if (isABFLBL) return false;
      allEqual = false;
    }

    if (isABFLPL) {
      allEqual = keys.every((key) => {
        const orig = (original as any)?.[key];
        const prop = (proposed as any)?.[key];

        if (
          prop === undefined ||
          prop === "" ||
          orig === undefined ||
          orig === "" ||
          Number(orig) === Number(prop)
        )
          return true;
      });
    }

    return allEqual ?? false;
  }, [formValues]);

  return (
    <>
      <Title
        title="Initiate Loan Deviation"
        close={() => props?.setIsSliderOpen(false)}
      />

      <div className="text-xs text-[#4C4C4D] font-normal ">
        You can commence Proposals for all available loan terms here
      </div>

      <form
        className="mt-10"
        onSubmit={handleSubmit(onSubmitHandler)}
      >
        <div className="flex justify-between items-center border-b pb-5 mb-5">
          <div className="text-sm font-semibold text-[#626262]">
            Loan Amount
          </div>
          <div className="text-xl font-semibold">
            {getFormattedCurrency(loanOffer?.amount)}
          </div>
        </div>

        <IntiateForm
          errors={errors}
          fields={FIELDS}
          isValid={isValid}
          register={register}
          getValues={getValues}
          loanOffer={loanOffer}
          textArea={{ label: "Remarks", name: "Remarks" }}
          isMFL_LAP={props?.isMFL_LAP}
          isABFLBL={isABFLBL}
          isABFLPL={isABFLPL}
        />

        <div className="mt-10 text-xs text-[#777878] font-normal">
          This action will submit the entire proposal for all the Loan terms
        </div>
        <Button
          type="submit"
          color="primary"
          disabled={isDisabled}
          className=" text-white mt-2"
        >
          Submit Proposal
        </Button>
      </form>
    </>
  );
}
